import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useCruiseSettings } from '../hooks/useCruiseSettings';
import { NAVY, GOLD } from '../utils/constants';
import { Bell } from 'lucide-react';

const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const parseDDMMYYYY = (s) => {
  const [d, m, y] = (s || '').split('-').map(Number);
  return new Date(y, m - 1, d);
};

const fmtDate = (ddmmyyyy) => {
  if (!ddmmyyyy) return '';
  const [d, m] = ddmmyyyy.split('-').map(Number);
  return `${d} ${SHORT_MONTHS[m - 1]}`;
};

export default function NotificationsBell({ onNavigate }) {
  const [unseated, setUnseated] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [open, setOpen]         = useState(false);
  // Active cruises only — paused-cruise enquiries correctly have no seat and
  // must not nag here; they reappear when the cruise is reactivated.
  const { settings: cruiseSettings } = useCruiseSettings();

  const fetchUnseated = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      const fmtDD = (d) => {
        const y = d.getFullYear(), m = d.getMonth() + 1, dd = d.getDate();
        return `${String(dd).padStart(2,'0')}-${String(m).padStart(2,'0')}-${y}`;
      };
      const dates30 = [];
      for (let i = 0; i <= 30; i++) {
        const d = new Date(now); d.setDate(now.getDate() + i);
        dates30.push(fmtDD(d));
      }

      const { data: upcoming } = await supabase
        .from('bookings')
        .select('id, booking_name, cruise_type, deck, date, adults, children, payment_status')
        .in('date', dates30)
        .neq('payment_status', 'Cancelled');

      const bkgIds = (upcoming || []).map(b => b.id);
      let seatedIds = new Set();
      if (bkgIds.length > 0) {
        const { data: seatingData } = await supabase
          .from('seating_assignments')
          .select('booking_id')
          .in('booking_id', bkgIds);
        seatedIds = new Set((seatingData || []).map(s => s.booking_id));
      }

      const activeNames = new Set((cruiseSettings || []).map(s => s.cruise_name));
      const result = (upcoming || [])
        .filter(b => !seatedIds.has(b.id))
        .filter(b => activeNames.has(b.cruise_type)) // exclude paused-cruise bookings
        .sort((a, b) => parseDDMMYYYY(a.date) - parseDDMMYYYY(b.date));

      setUnseated(result);
    } catch (_) {
      // silently ignore
    } finally {
      setLoading(false);
    }
  }, [cruiseSettings]);

  // Initial load + 5-min polling
  useEffect(() => {
    fetchUnseated();
    const interval = setInterval(fetchUnseated, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchUnseated]);

  // Real-time: re-fetch on any change to seating_assignments or bookings
  useEffect(() => {
    const channel = supabase
      .channel('bell-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'seating_assignments' }, () => {
        fetchUnseated();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bookings' }, () => {
        fetchUnseated();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchUnseated]);

  const total = unseated.length;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="Unseated Bookings"
        aria-label="Unseated bookings"
        style={{
          position: 'relative',
          background: 'none',
          border: '1px solid var(--color-border-strong)',
          borderRadius: 7,
          color: 'var(--color-ink-secondary)',
          cursor: 'pointer',
          padding: '5px 10px',
          flexShrink: 0,
          lineHeight: 1,
          display: 'inline-flex',
          alignItems: 'center',
          transition: 'color 0.15s, border-color 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.color = '#C9A84C'; e.currentTarget.style.borderColor = '#C9A84C'; }}
        onMouseLeave={e => { e.currentTarget.style.color = 'var(--color-ink-secondary)'; e.currentTarget.style.borderColor = 'var(--color-border-strong)'; }}
      >
        <Bell size={18} strokeWidth={1.75} aria-hidden="true" />
        {total > 0 && (
          <span style={{
            position: 'absolute', top: -5, right: -5,
            background: '#dc2626', color: '#fff',
            borderRadius: '50%', minWidth: 17, height: 17,
            fontSize: '0.58rem', fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1.5px solid #fff', padding: '0 3px',
            lineHeight: 1, boxSizing: 'border-box',
          }}>
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      {open && (
        <div className="notif-modal-backdrop" onClick={e => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="notif-modal">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px 14px', borderBottom: '1px solid var(--color-border-subtle)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h2 style={{ fontSize: '1rem', fontWeight: 800, color: NAVY, margin: 0 }}>Notifications</h2>
                {loading && <span style={{ fontSize: '0.65rem', color: 'var(--color-ink-faint)' }}>Updating…</span>}
                {total > 0 && !loading && (
                  <span style={{ background: '#dc2626', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: '0.65rem', fontWeight: 800 }}>{total}</span>
                )}
              </div>
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: 'var(--color-ink-muted)', padding: '0 4px', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '16px 22px 20px' }}>
              {total === 0 && !loading && (
                <div style={{ fontSize: '0.85rem', color: 'var(--color-ink-faint)', textAlign: 'center', padding: '36px 0' }}>
                  All good — no unseated bookings
                </div>
              )}
              {unseated.length > 0 && (
                <div>
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--color-ink-faint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
                    Unseated Bookings — Next 30 Days ({total})
                  </div>
                  {unseated.map((b, i, arr) => {
                    const pax = (b.adults || 0) + (b.children || 0);
                    return (
                      <div key={b.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--color-border-subtle)' : 'none' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.88rem', fontWeight: 700, color: NAVY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.booking_name}</div>
                          <div style={{ fontSize: '0.73rem', color: '#6b7280', marginTop: 2 }}>
                            {b.cruise_type}{b.deck ? ` · ${b.deck}` : ''} · {fmtDate(b.date)} · {pax} pax
                            {b.payment_status === 'FOC' && <span style={{ marginLeft: 6, fontSize: '0.65rem', fontWeight: 700, color: '#166534', background: '#dcfce7', borderRadius: 4, padding: '1px 5px' }}>FOC</span>}
                          </div>
                        </div>
                        <button
                          onClick={() => { setOpen(false); onNavigate('seating-sheet', b.date, { cruise_type: b.cruise_type, highlight_booking_id: b.id }); }}
                          style={{ flexShrink: 0, marginLeft: 12, fontSize: '0.72rem', fontWeight: 700, color: GOLD, background: 'none', border: `1px solid ${GOLD}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          → Assign
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
